import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { KanbanApiService } from '../../services/kanban-api/KanbanApiService';
import {
  KanbanTask,
  KanbanTaskStatus,
  SaveKanbanTaskData,
  WorkspacePriority,
} from '../../types/Workspace';

interface KanbanColumn {
  status: KanbanTaskStatus;
  label: string;
  tone: string;
}

interface KanbanTaskForm {
  title: string;
  description: string;
  priority: WorkspacePriority;
  status: KanbanTaskStatus;
  dueDate: string;
}

@Component({
  selector: 'app-kanban-page',
  standalone: false,
  templateUrl: './kanban-page.component.html',
  styleUrl: './kanban-page.component.sass',
})
export class KanbanPageComponent implements OnInit {
  public tasks: KanbanTask[] = [];
  public formData: KanbanTaskForm = this.createForm();
  public tagsInput = '';
  public modalOpen = false;
  public loading = false;
  public saving = false;
  public movingTaskId: string | null = null;
  public draggedTaskId: string | null = null;
  public dragOverStatus: KanbanTaskStatus | null = null;
  public errorMessage: string | null = null;
  public successMessage: string | null = null;

  public readonly columns: KanbanColumn[] = [
    {
      status: 'queue',
      label: 'Очередь',
      tone: 'queue',
    },
    {
      status: 'in_progress',
      label: 'В работе',
      tone: 'progress',
    },
    {
      status: 'review',
      label: 'На проверке',
      tone: 'review',
    },
    {
      status: 'done',
      label: 'Готово',
      tone: 'done',
    },
  ];

  public readonly priorityOptions: {
    value: WorkspacePriority;
    label: string;
  }[] = [
    {
      value: 'low',
      label: 'Низкий',
    },
    {
      value: 'medium',
      label: 'Обычный',
    },
    {
      value: 'high',
      label: 'Высокий',
    },
    {
      value: 'urgent',
      label: 'Срочный',
    },
  ];

  public constructor(
    private kanbanApi: KanbanApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.loadTasks();
  }

  public openCreate(status: KanbanTaskStatus = 'queue'): void {
    this.formData = this.createForm(status);
    this.tagsInput = '';
    this.modalOpen = true;
  }

  public closeModal(): void {
    if (!this.saving) {
      this.modalOpen = false;
    }
  }

  public async saveTask(): Promise<void> {
    if (!this.formData.title.trim()) {
      this.errorMessage = 'Укажите название задачи.';
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    try {
      const response = await this.kanbanApi.createKanbanTask(this.toSaveData());
      const task = this.unwrapResponse(response);
      if (!task) {
        return;
      }

      this.tasks = [
        task,
        ...this.tasks,
      ];
      this.notificationService.notify('kanban', 'Задача добавлена', task.title);
      this.successMessage = 'Задача добавлена.';
      this.modalOpen = false;
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public onDragStart(event: DragEvent, task: KanbanTask): void {
    this.draggedTaskId = task.id;
    event.dataTransfer?.setData('text/plain', task.id);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  public onDragEnd(): void {
    this.draggedTaskId = null;
    this.dragOverStatus = null;
  }

  public onDragOver(event: DragEvent, status: KanbanTaskStatus): void {
    event.preventDefault();
    this.dragOverStatus = status;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  public async onDrop(event: DragEvent, status: KanbanTaskStatus): Promise<void> {
    event.preventDefault();
    const taskId = event.dataTransfer?.getData('text/plain') ?? this.draggedTaskId;
    const task = this.tasks.find((item) => item.id === taskId);
    this.draggedTaskId = null;
    this.dragOverStatus = null;
    if (task) {
      await this.moveTask(task, status);
    }
  }

  public async moveTask(task: KanbanTask, status: KanbanTaskStatus): Promise<void> {
    if (task.status === status || this.movingTaskId) {
      return;
    }

    this.movingTaskId = task.id;
    try {
      const response = await this.kanbanApi.updateKanbanTask(task.id, { status });
      const updatedTask = this.unwrapResponse(response);
      if (updatedTask) {
        this.replaceTask(updatedTask);
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.movingTaskId = null;
      this.changeDetector.detectChanges();
    }
  }

  public async deleteTask(task: KanbanTask): Promise<void> {
    if (!window.confirm('Удалить задачу "' + task.title + '"?')) {
      return;
    }

    try {
      const response = await this.kanbanApi.deleteKanbanTask(task.id);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }

      this.tasks = this.tasks.filter((item) => item.id !== task.id);
      this.notificationService.notify('kanban', 'Задача удалена', task.title);
      this.successMessage = 'Задача удалена.';
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public getColumnTasks(status: KanbanTaskStatus): KanbanTask[] {
    return this.tasks.filter((task) => task.status === status);
  }

  public getPriorityName(priority: WorkspacePriority): string {
    return this.priorityOptions.find((item) => item.value === priority)?.label ?? 'Обычный';
  }

  public getTagTone(tag: string): string {
    const normalizedTag = tag.toLowerCase();
    if ([
      'backend',
      'api',
      'сервер',
    ].includes(normalizedTag)) {
      return 'violet';
    }
    if ([
      'frontend',
      'ui',
      'клиент',
    ].includes(normalizedTag)) {
      return 'cyan';
    }
    if ([
      'design',
      'дизайн',
    ].includes(normalizedTag)) {
      return 'pink';
    }
    if ([
      'bug',
      'ошибка',
      'срочно',
    ].includes(normalizedTag)) {
      return 'red';
    }
    if ([
      'analytics',
      'аналитика',
    ].includes(normalizedTag)) {
      return 'amber';
    }
    return 'teal';
  }

  public formatDueDate(value: string | null): string {
    if (!value) {
      return '';
    }
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: 'short',
    }).format(new Date(value + 'T00:00:00'));
  }

  public getStatusFromEvent(event: Event): KanbanTaskStatus {
    return (event.target as HTMLSelectElement).value as KanbanTaskStatus;
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  private async loadTasks(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;
    try {
      const response = await this.kanbanApi.getKanbanTasks();
      const tasks = this.unwrapResponse(response);
      if (tasks) {
        this.tasks = tasks;
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private createForm(status: KanbanTaskStatus = 'queue'): KanbanTaskForm {
    return {
      title: '',
      description: '',
      priority: 'medium',
      status,
      dueDate: '',
    };
  }

  private toSaveData(): SaveKanbanTaskData {
    return {
      title: this.formData.title.trim(),
      description: this.formData.description.trim() || null,
      priority: this.formData.priority,
      status: this.formData.status,
      tags: this.tagsInput
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
      dueDate: this.formData.dueDate || null,
    };
  }

  private replaceTask(updatedTask: KanbanTask): void {
    this.tasks = this.tasks.map((task) => task.id === updatedTask.id ? updatedTask : task);
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }
}
